// AI Service - Demo Mode
// Uses mock data instead of real API calls for demonstration purposes

import {
  getMockPost,
  getMockRefinedPost,
  getMockHashtags,
  getMockImagePrompts,
  saveMockPost,
  getSavedPosts,
  deleteMockPost,
  updateMockPost,
  isDemoMode
} from '../data/mockResponses.js';
import SettingsService from './settingsService.js';

class AIService {
  // Check if we're in demo mode
  static isDemoMode() {
    return isDemoMode();
  }

  static async generatePost(data) {
    console.log('Demo Mode: Generating post with params:', data);

    try {
      // Get preferences from settings (for display purposes)
      const preferences = SettingsService.getPreferences();

      // Generate mock post based on language and tone
      const mockPost = await getMockPost({
        language: data.language || 'english',
        tone: data.tone || 'professional',
        title: data.title,
        customTopics: data.customTopics
      });

      return {
        success: true,
        data: mockPost,
        demo: true,
        message: 'This is a demo response. Connect to a real AI backend for custom content.'
      };
    } catch (error) {
      console.error('Mock generation error:', error);
      return {
        success: false,
        error: {
          message: 'Failed to generate demo post. Please try again.'
        }
      };
    }
  }

  static async regeneratePost(postId) {
    console.log('Demo Mode: Regenerating post:', postId);

    try {
      // Generate a new mock post
      const mockPost = await getMockPost({
        language: 'english',
        tone: 'professional'
      });

      return {
        success: true,
        data: mockPost,
        demo: true
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: 'Failed to regenerate demo post.'
        }
      };
    }
  }

  static async refinePost(content, feedback) {
    console.log('Demo Mode: Refining post with feedback:', feedback);

    try {
      const refinedPost = await getMockRefinedPost(content, feedback);

      return {
        success: true,
        data: refinedPost,
        demo: true
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: 'Failed to refine demo post.'
        }
      };
    }
  }

  static async savePost(postData) {
    console.log('Demo Mode: Saving post:', postData);

    try {
      const savedPost = await saveMockPost(postData);

      return {
        success: true,
        data: savedPost,
        demo: true
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: 'Failed to save demo post.'
        }
      };
    }
  }

  static async getSavedPosts() {
    console.log('Demo Mode: Getting saved posts');

    try {
      const posts = await getSavedPosts();

      return {
        success: true,
        data: posts,
        demo: true
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: 'Failed to load saved posts.'
        }
      };
    }
  }

  static async deletePost(postId) {
    console.log('Demo Mode: Deleting post:', postId);

    try {
      await deleteMockPost(postId);

      return {
        success: true,
        demo: true
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: 'Failed to delete demo post.'
        }
      };
    }
  }

  static async updatePost(postId, updates) {
    console.log('Demo Mode: Updating post:', postId, updates);

    try {
      const updatedPost = await updateMockPost(postId, updates);

      return {
        success: true,
        data: updatedPost,
        demo: true
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: 'Failed to update demo post.'
        }
      };
    }
  }

  static async generateImagePrompts(data) {
    console.log('Demo Mode: Generating image prompts');

    try {
      const prompts = await getMockImagePrompts();

      // Format prompts in the expected format
      const formattedPrompts = prompts.map((prompt, index) =>
        `[PROMPT_${index + 1}]\n${prompt}\n[/PROMPT_${index + 1}]`
      ).join('\n\n');

      return {
        success: true,
        data: {
          prompts: formattedPrompts
        },
        demo: true
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: 'Failed to generate demo image prompts.'
        }
      };
    }
  }

  static async generateHashtags(title, content) {
    console.log('Demo Mode: Generating hashtags');

    try {
      const hashtags = await getMockHashtags(title, content);

      return {
        success: true,
        data: {
          hashtags: hashtags
        },
        demo: true
      };
    } catch (error) {
      return {
        success: false,
        error: {
          message: 'Failed to generate demo hashtags.'
        }
      };
    }
  }

  static async generateImage(postId, content, customPrompt = null) {
    console.log('Demo Mode: Image generation not available in demo');

    // Return a placeholder message for demo mode
    return {
      success: false,
      demo: true,
      error: {
        message: 'Image generation is not available in demo mode. Connect to a real backend with AI image generation capabilities.'
      }
    };
  }

  static async checkImageServiceStatus() {
    // In demo mode, image service is not available
    return {
      success: true,
      data: {
        available: false,
        message: 'Demo mode - Image generation requires a real backend'
      },
      demo: true
    };
  }

  static async testAI() {
    console.log('Demo Mode: Testing AI connection');

    return {
      success: true,
      data: {
        status: 'demo',
        message: 'Running in demo mode. All responses are pre-generated samples.',
        features: {
          postGeneration: true,
          postRefinement: true,
          hashtagGeneration: true,
          imagePrompts: true,
          imageGeneration: false
        }
      },
      demo: true
    };
  }
}

export default AIService;
